import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useToggleDetailOverlay from '../../../hooks/useToggleDetailOverlay';
import { getUserIP } from '../../../utils/user';
import { PPV_PURCHASE_OPTIONS } from '@intigral/constant';
import user from '../../../helpers/user';
import { ppvGenerateOTPApi } from '../../../services/serviceWrapper/ppvServiceWrapper';
import { __ } from '../../../translations';
import { container } from '@intigral/domain';
import { DI } from '@intigral/constant/config/DIContainer';
import url from '../../../helpers/url';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { ISubscriptionService } from '@intigral/domain/lib/application/services/SubscriptionService';
import { useQueryClient } from '@tanstack/react-query';
import { isFreshBasicOperator, isUserInGracePeriod } from '@intigral/utils';
import { validEmail } from '../../../helpers/validator';
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';

export const usePPVHandlers = ({
    selectedPPVEvent,
    selectedChannel,
    formRef,
    setSelectedProgram,
    setPpvSubscribedList,
    setSelectedPPVId,
    setShowppvPurchaseOverlay,
    setShowppvQrModal,
}) => {
    const { closePpvOverlay } = useToggleDetailOverlay();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { track } = useContext(MixpanelContext);

    const addQueryParams = (params: Record<string, string>) => {
        const currentParams = new URLSearchParams(window.location.search);
        Object.entries(params).forEach(([key, value]) => {
            currentParams.set(key, value);
        });
        navigate(`${window.location.pathname}?${currentParams.toString()}`);
    };

    const getProgramByGuid = async (byGuid) => {
        const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
        const result = await mpxService.getFeed('search-all-pa', { byGuid });
        if (result?.entries.length) {
            const programData = result?.entries?.[0] ?? null;
            setSelectedProgram({
                ...programData,
                paId: programData?.id,
                isPpv: true,
                name: selectedPPVEvent?.name,
                eventId: selectedPPVEvent?.eventId,
            });
        }
    };

    const handleCCPaymentCompleted = async () => {
        const { authToken, username, identityToken } = user.getUserInfo() || {};
        const subscriptionService = container.resolve<ISubscriptionService>(DI.SERVICES.ISubscriptionService);
        const subscription = await subscriptionService.getSubscriptionStatus(
            user.getAPIKey(),
            username,
            authToken,
            identityToken
        );
        if (subscription) {
            user.updateUserSubscriptionsObject(subscription, false);
            const { ppvList } = subscription;
            setPpvSubscribedList(ppvList);
        }
    };

    const onPurchaseSuccess = (data) => {
        if (data) {
            setPpvSubscribedList(data);
        }
        setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['profiles', 'user-reminders'] });
            queryClient.invalidateQueries({ queryKey: ['remindersList', 'PPV'] });
            track(EVENT.PPV.PPV_REMINDER_SET, {
                'Event Title': selectedPPVEvent?.name,
                'Event ID': selectedPPVEvent?.eventId,
            });
        }, 3000);
    };

    const handleCloseOverlay = () => {
        window.history.replaceState(null, '', window.location.pathname);
        setSelectedProgram(null);
        setSelectedPPVId(null);
        closePpvOverlay();
    };

    const handleClickPlay = () => {
        const redirectUrl = url.route('livetv', {
            channelId: btoa(selectedChannel.Id || selectedChannel?.channelID),
        });
        navigate(redirectUrl);
    };

    const handleClickPurchase = async () => {
        if (isUserInGracePeriod()) {
            dispatch(showUpgradedModal(true));
            return;
        }
        const userInfo = user.getUserInfo();
        const operator = userInfo?.operator;
        const paymentType = user.getPPVPurchaseOption(operator);
        if (paymentType === PPV_PURCHASE_OPTIONS.IAP) {
            setShowppvQrModal(true);
        } else if (
            paymentType === PPV_PURCHASE_OPTIONS.CC ||
            (isFreshBasicOperator(operator) && validEmail(userInfo?.username))
        ) {
            // if user is free tier and username is email, then show CC payment
            track(EVENT.PPV.PPV_PURCHASED_STARTED, {
                'Event Title': selectedPPVEvent?.name,
                'Event ID': selectedPPVEvent?.eventId,
                'Purchase Method': 'CC',
            });
            getUserIP().then((userIP) => {
                formRef.current?.querySelector('input[name="customerIp"]')?.setAttribute('value', userIP || '');
                formRef.current?.submit();
            });
        } else if (
            paymentType === PPV_PURCHASE_OPTIONS.OTP ||
            paymentType === PPV_PURCHASE_OPTIONS.PASSWORD ||
            paymentType === PPV_PURCHASE_OPTIONS.PIN
        ) {
            if (operator === 'STC_HOME') {
                setShowppvPurchaseOverlay(true);
                return;
            }
            const response: any = await ppvGenerateOTPApi({ eventData: selectedPPVEvent });
            if (response?.status === 'OTP_SUCCESS') {
                setShowppvPurchaseOverlay(true);
            }
        }
    };

    return {
        addQueryParams,
        getProgramByGuid,
        handleCCPaymentCompleted,
        onPurchaseSuccess,
        handleCloseOverlay,
        handleClickPlay,
        handleClickPurchase,
    };
};
